if (!customElements.get('venezuela-presence-map')) {
  customElements.define(
    'venezuela-presence-map',
    class VenezuelaPresenceMap extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onPointerOver = this.onPointerOver.bind(this);
        this.onPointerOut = this.onPointerOut.bind(this);
        this.onEditorBlockSelect = this.onEditorBlockSelect.bind(this);
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.states = Array.from(this.querySelectorAll('[data-map-state]'));
        this.markers = Array.from(this.querySelectorAll('[data-map-marker]'));
        this.locationButtons = Array.from(this.querySelectorAll('[data-location-button]'));
        this.operationButtons = Array.from(this.querySelectorAll('[data-operation-button]'));
        this.offices = Array.from(this.querySelectorAll('[data-office]'));
        this.operations = Array.from(this.querySelectorAll('[data-operation]'));
        this.callouts = Array.from(this.querySelectorAll('[data-map-callout]'));
        this.tabs = Array.from(this.querySelectorAll('[data-map-mode]'));
        this.panels = Array.from(this.querySelectorAll('[data-map-panel]'));
        this.tooltip = this.querySelector('[data-map-tooltip]');

        const stateLabels = new Map();
        this.locationButtons.forEach((button) => {
          this.stateList(button).forEach((slug) => {
            if (!stateLabels.has(slug)) stateLabels.set(slug, button.dataset.stateLabel);
          });
        });
        this.stateLabels = stateLabels;

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('pointerover', this.onPointerOver);
        this.addEventListener('pointerout', this.onPointerOut);
        document.addEventListener('shopify:block:select', this.onEditorBlockSelect);

        this.applyMode(this.dataset.mode === 'operations' ? 'operations' : 'sedes');
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeydown);
        this.removeEventListener('pointerover', this.onPointerOver);
        this.removeEventListener('pointerout', this.onPointerOut);
        document.removeEventListener('shopify:block:select', this.onEditorBlockSelect);
        this.initialized = false;
      }

      stateList(element) {
        return (element.dataset.states || element.dataset.state || '')
          .split(',')
          .map((slug) => slug.trim())
          .filter(Boolean);
      }

      get items() {
        return this.mode === 'operations' ? this.operations : this.offices;
      }

      findItem(state, id = '') {
        if (id) {
          const byId = this.items.find((item) => (item.dataset.officeId || item.dataset.operationId) === id);
          if (byId) return byId;
        }
        return this.items.find((item) => this.stateList(item).includes(state));
      }

      applyMode(mode) {
        this.mode = mode;
        this.dataset.mode = mode;
        this.tabs.forEach((tab) => {
          const selected = tab.dataset.mapMode === mode;
          tab.setAttribute('aria-selected', String(selected));
          tab.setAttribute('tabindex', selected ? '0' : '-1');
        });
        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.mapPanel !== mode;
        });

        this.states.forEach((state) => {
          const slug = state.dataset.state;
          const stateName = this.stateLabels.get(slug) || state.dataset.stateName;
          const matches = this.items.filter((item) => this.stateList(item).includes(slug)).length;
          const interactive = matches > 0;
          state.dataset.interactive = interactive ? 'true' : 'false';
          if (interactive) {
            const office = this.dataset.i18nOffice || 'Sede';
            const offices = this.dataset.i18nOffices || 'Sedes';
            const detail = mode === 'sedes' ? `${matches} ${matches === 1 ? office : offices}` : this.dataset.i18nOperations || 'Operaciones';
            state.setAttribute('tabindex', '0');
            state.setAttribute('aria-label', `${stateName}: ${detail}`);
          } else {
            state.setAttribute('tabindex', '-1');
            state.removeAttribute('aria-label');
          }
        });

        this.pinned = null;
        this.render(null);
      }

      getControl(target) {
        if (!(target instanceof Element)) return null;
        return target.closest('[data-map-mode], [data-map-state], [data-map-marker], [data-location-button], [data-operation-button]');
      }

      getPreviewControl(target) {
        if (!(target instanceof Element)) return null;
        return target.closest('[data-office], [data-operation]') || this.getControl(target);
      }

      isPreviewable(control) {
        if (!control || !this.contains(control) || control.hasAttribute('data-map-mode')) return false;
        return !(control.hasAttribute('data-map-state') && control.dataset.interactive !== 'true');
      }

      onClick(event) {
        const control = this.getControl(event.target);
        if (control && this.contains(control) && control.hasAttribute('data-map-mode')) {
          if (control.dataset.mapMode !== this.mode) this.applyMode(control.dataset.mapMode);
          return;
        }
        if (!this.isPreviewable(control)) {
          if (event.target instanceof Element && event.target.closest('.venezuela-presence-map__map-wrap')) {
            this.pinned = null;
            this.render(null);
          }
          return;
        }
        this.pin(control);
      }

      onKeydown(event) {
        const tab = event.target.closest('[data-map-mode]');
        if (tab && this.contains(tab) && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const index = this.tabs.indexOf(tab);
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          this.applyMode(this.tabs[next].dataset.mapMode);
          this.tabs[next].focus();
          return;
        }
        const state = event.target.closest('[data-map-state]');
        if (!state || !this.contains(state) || state.dataset.interactive !== 'true') return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.pin(state);
        }
      }

      onPointerOver(event) {
        if (event.pointerType === 'touch') return;
        const control = this.getPreviewControl(event.target);
        if (!this.isPreviewable(control)) return;
        if (this.getPreviewControl(event.relatedTarget) === control) return;
        this.render(this.resolve(control));
      }

      onPointerOut(event) {
        if (event.pointerType === 'touch') return;
        const control = this.getPreviewControl(event.target);
        if (!control || !this.contains(control)) return;
        if (this.getPreviewControl(event.relatedTarget) === control) return;
        this.render(this.pinned);
      }

      onEditorBlockSelect(event) {
        const id = event.detail.blockId;
        const office = this.offices.find((item) => item.dataset.officeId === id);
        const operation = this.operations.find((item) => item.dataset.operationId === id);
        if (!office && !operation) return;
        const mode = office ? 'sedes' : 'operations';
        if (this.mode !== mode) this.applyMode(mode);
        this.pin(office || operation);
      }

      resolve(control) {
        const id = control.dataset.officeId || control.dataset.operationId || '';
        const item = this.findItem(control.dataset.state, id);
        if (!item) return null;
        const hovered = control.hasAttribute('data-map-state') ? control.dataset.state : this.stateList(item)[0];
        return { item, hovered };
      }

      pin(control) {
        this.pinned = this.resolve(control);
        this.render(this.pinned);
      }

      render(selection) {
        this.classList.add('is-enhanced');
        const item = selection ? selection.item : null;
        const selectedStates = new Set(item ? this.stateList(item) : []);
        const officeId = item && item.dataset.officeId ? item.dataset.officeId : '';

        this.states.forEach((state) => {
          const selected = selectedStates.has(state.dataset.state);
          state.classList.toggle('is-selected', selected);
          state.setAttribute('aria-pressed', String(selected));
        });
        this.markers.forEach((marker) => {
          const selected = marker.dataset.officeId === officeId;
          marker.classList.toggle('is-selected', selected);
          marker.setAttribute('aria-current', selected ? 'location' : 'false');
        });
        [...this.offices, ...this.operations].forEach((entry) => {
          const selected = entry === item;
          entry.classList.toggle('is-selected', selected);
          entry.setAttribute('aria-current', selected ? 'location' : 'false');
        });
        [...this.locationButtons, ...this.operationButtons].forEach((button) => {
          const selected = item ? (button.dataset.officeId || button.dataset.operationId) === (item.dataset.officeId || item.dataset.operationId) : false;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', String(selected));
        });
        this.callouts.forEach((callout) => {
          callout.hidden = !officeId || callout.dataset.officeId !== officeId;
        });
        this.renderTooltip(this.mode === 'operations' && item ? selection : null);
      }

      renderTooltip(selection) {
        if (!this.tooltip) return;
        const state = selection && this.states.find((path) => path.dataset.state === selection.hovered);
        if (!state) {
          this.tooltip.hidden = true;
          return;
        }
        const svg = state.ownerSVGElement;
        const box = state.getBBox();
        const view = svg.viewBox.baseVal;
        const button = selection.item.querySelector('[data-operation-button]');
        const heading = document.createElement('h3');
        heading.textContent = (button && button.dataset.label) || state.dataset.stateName;
        this.tooltip.replaceChildren(heading);
        if (button && button.dataset.detail) {
          const detail = document.createElement('p');
          detail.textContent = button.dataset.detail;
          this.tooltip.append(detail);
        }
        const x = ((box.x + box.width / 2 - view.x) / view.width) * 100;
        const y = ((box.y + box.height / 2 - view.y) / view.height) * 100;
        this.tooltip.style.setProperty('--marker-x', `${x}%`);
        this.tooltip.style.setProperty('--marker-y', `${y}%`);
        this.tooltip.classList.toggle('venezuela-presence-map__callout--above', y > 52);
        this.tooltip.hidden = false;
      }
    }
  );
}
